import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { ArrowLeft, Phone, Mail, MapPin, Clock, Building2, ArrowRight, Loader2, CheckCircle, AlertCircle, Calculator } from 'lucide-react';
import { ProjectEstimator } from '../components/ProjectEstimator';
import { sendConsultationEmail, ConsultationFormData } from '../lib/emailjs';
import { trackEvent } from '../lib/analytics';
import { motion } from 'framer-motion';

const services = [
  'New Construction',
  'Commercial Construction',
  'Home Building',
  'Renovations',
  'Construction Management',
  'Site Development',
  'Interior Build-outs',
  'Project Planning',
  'Design-Build Services',
  'Sustainable Building',
  'Historic Restoration',
  'Emergency Services'
];

const benefits = [
  {
    title: 'Licensed in NY & CT',
    description: 'Fully licensed and insured in both Westchester County, NY and Fairfield County, CT.'
  },
  {
    title: 'Detailed Planning',
    description: 'Comprehensive project plans with transparent cost estimates and timelines.'
  },
  {
    title: 'Quality Materials',
    description: 'Access to premium construction materials and trusted supplier networks.'
  },
  {
    title: 'Expert Team',
    description: 'Skilled craftsmen, licensed professionals, and experienced project managers.'
  }
];

export function ConsultationPage() {
  const [formData, setFormData] = useState<ConsultationFormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    location: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await sendConsultationEmail(formData);
      trackEvent('generate_lead', {
        method: 'consultation_form',
        page_path: window.location.pathname,
      });
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        location: '',
        budget: '',
        timeline: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Free Renovation Consultation | Westchester & Fairfield Counties</title>
        <meta name="description" content="Schedule a free consultation for your renovation project in Westchester County, NY or Fairfield County, CT. Licensed and insured renovation contractor serving both states." />
        <meta name="keywords" content="renovation consultation, free estimate, renovation contractor consultation, Westchester renovation, Fairfield renovation" />
        <link rel="canonical" href="https://renovationwestchester.com/consultation" />
      </Helmet>

      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Home</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Free Renovation Consultation
              </h1>
              <p className="text-zinc-400 mb-12 leading-relaxed">
                Licensed and insured in both Westchester County, NY and Fairfield County, CT. 
                Our experienced team is ready to discuss your renovation project, whether it's 
                kitchen remodeling, bathroom renovation, or complete home improvement.
              </p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-12 relative group"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 rounded-xl blur-lg opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                <Link
                  to="/project-planner"
                  className="relative flex items-center gap-4 bg-gradient-to-r from-zinc-900 to-zinc-800 p-6 rounded-xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300"
                >
                  <div className="p-3 bg-yellow-400/10 rounded-lg">
                    <Calculator className="h-8 w-8 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl text-zinc-100 mb-1">Try Our Project Planner</h3>
                    <p className="text-zinc-400">Get an instant AI-powered estimate for your project</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-yellow-400 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>

              <div className="space-y-8 mb-12">
                <div className="flex items-center gap-6">
                  <Phone className="h-6 w-6 text-yellow-400" />
                  <a href="tel:9149606433" className="text-zinc-400 hover:text-yellow-400 transition-colors">
                    (914) 960-6433
                  </a>
                </div>
                <div className="flex items-center gap-6">
                  <Mail className="h-6 w-6 text-yellow-400" />
                  <a href="mailto:info@renovationwestchester.com" className="text-zinc-400 hover:text-yellow-400 transition-colors">
                    info@renovationwestchester.com
                  </a>
                </div>
                <div className="flex items-center gap-6">
                  <MapPin className="h-6 w-6 text-yellow-400" />
                  <span className="text-zinc-400">Serving Westchester County, NY & Fairfield County, CT</span>
                </div>
                <div className="flex items-center gap-6">
                  <Clock className="h-6 w-6 text-yellow-400" />
                  <span className="text-zinc-400">Mon-Fri: 7:00 AM - 7:00 PM</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-8 mb-12">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-zinc-800/30 p-6 rounded-xl">
                    <h3 className="text-lg text-zinc-100 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-zinc-400">{benefit.description}</p>
                  </div>
                ))}
              </div>

              <ProjectEstimator />
            </div>

            <form onSubmit={handleSubmit} className="bg-zinc-800/30 p-8 rounded-xl space-y-6 sticky top-24">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm text-zinc-400 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-zinc-400 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field rounded-xl"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm text-zinc-400 mb-2">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field rounded-xl"
                  required
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm text-zinc-400 mb-2">Type of Service Needed</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="input-field rounded-xl"
                  required
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                  <option value="other">Other (Please specify in message)</option>
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm text-zinc-400 mb-2">Project Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="input-field rounded-xl"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="budget" className="block text-sm text-zinc-400 mb-2">Estimated Budget</label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="input-field rounded-xl"
                    required
                  >
                    <option value="">Select range</option>
                    <option value="under-10k">Under $10,000</option>
                    <option value="10k-20k">$10,000 - $20,000</option>
                    <option value="20k-30k">$20,000 - $30,000</option>
                    <option value="30k-40k">$30,000 - $40,000</option>
                    <option value="40k-50k">$40,000 - $50,000</option>
                    <option value="50k-60k">$50,000 - $60,000</option>
                    <option value="60k-70k">$60,000 - $70,000</option>
                    <option value="70k-80k">$70,000 - $80,000</option>
                    <option value="80k-90k">$80,000 - $90,000</option>
                    <option value="90k-100k">$90,000 - $100,000</option>
                    <option value="100k-plus">$100,000+</option>
                    <option value="unknown">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="timeline" className="block text-sm text-zinc-400 mb-2">Desired Timeline</label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="input-field rounded-xl"
                    required
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Ready to start</option>
                    <option value="1-3">1-3 months</option>
                    <option value="3-6">3-6 months</option>
                    <option value="6-12">6-12 months</option>
                    <option value="12+">12+ months</option>
                    <option value="planning">Just planning</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-zinc-400 mb-2">Project Details</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="input-field rounded-xl"
                  required
                  placeholder="Please describe your project, including any specific requirements or concerns..."
                ></textarea>
              </div>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-2 text-green-400 bg-green-400/10 p-4 rounded-xl">
                  <CheckCircle className="h-5 w-5 flex-shrink-0" />
                  <p>Your consultation request has been sent successfully! We'll contact you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-400 bg-red-400/10 p-4 rounded-xl">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <p>There was an error sending your request. Please try again or call us directly.</p>
                </div>
              )}

              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-yellow-400 text-zinc-900 px-8 py-4 text-sm tracking-wider hover:bg-yellow-500 transition-colors duration-300 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending Request...
                  </>
                ) : (
                  <>
                    Submit Consultation Request
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}